/**
 * The Compare surfaces form: its zod schema, its value type and its defaults.
 *
 * One form feeds both engines. Fields are grouped by what they belong to:
 *   - `inputs`   — the two roles (A = cut & fill `top` / deviation reference,
 *                  B = `bottom` / deviation source) and the boundary. The
 *                  `${side}LayerId/SurfaceIds/PcMethod` names are
 *                  `SurfacePicker`'s contract (prefix `inputs.top`).
 *   - `cutFill`  — the surface-difference settings.
 *   - ROOT       — the station-volume and expectation fields, under the very
 *                  names both legacy dialogs used, so `StationVolumesAddOn` /
 *                  `applyStationPatch`, `ExpectationFields` and its
 *                  `SurfacePicker`s (`expectUpper` / `expectLower`) work
 *                  unchanged (ruling R15). They belong to cut & fill only
 *                  (spec goal 3): the schema and the payloads gate them on
 *                  `results.includes('cutFill')`.
 *   - `deviation`— the deviation engine's params.
 *   - `addOns`   — GeoTIFF / DXF / PDF switches and the report fields.
 *   - `output`   — the run name.
 *
 * Validation messages are TAGS (`COMPARE_VALIDATION_TAGS`), never copy: the
 * dialog maps them to translated text in one `onInvalid` map.
 */
import { z } from 'zod';

import { PC_METHODS } from '@/components/SurfacePicker';
import { isPointcloudLayer, type LayerLike } from '@/lib/analyses/candidates';
import { defaultRampName } from '@/lib/cutFillConvention';
import { reportLocaleFor, SUPPORTED_REPORT_LOCALES } from '@/lib/reportLocales';
import { refineStationVolumes, stationVolumesDefaults, stationVolumesZodShape } from '@/lib/stationVolumes/dialogModel';
import { expectationDefaults, expectationZodShape, refineExpectation } from '@/lib/surfaceExpectation';

import { COMPARE_KINDS, COMPARE_RESULTS, DEVIATION_MODES, type CompareResult } from './availability';
import type { RememberedSettings } from './rememberedSettings';

export type PcMethod = (typeof PC_METHODS)[number];

/** Spec §12: the values new runs start from. */
export const CUT_FILL_GRID_DEFAULT_M = 0.25;
export const NEW_RUN_PC_METHOD: PcMethod = 'median';
/** What a legacy run without a stored sampling method used. */
export const LEGACY_PC_METHOD: PcMethod = 'min';
export const DEVIATION_RANGE_DEFAULT_M = 0.05;
export const DEVIATION_TOLERANCE_DEFAULT_M = 0.01;
/** A point-cloud design's tolerance: a DSM reference is coarser (legacy PC↔PC). */
export const DEVIATION_PC_TOLERANCE_DEFAULT_M = 0.05;

/**
 * The tolerance a deviation run sends. Only the volume statistics read it
 * (`deviation.py`'s volume-stats path), and horizontal mode turns those off,
 * so there an invalid stored value — the field is disabled — is replaced by
 * the default rather than refused (ruling R22).
 */
export function effectiveDeviationTolerance(
  d: { mode: string; volumeTolerance: number },
  designIsCloud: boolean,
): number {
  if (d.mode !== 'horizontal' || (Number.isFinite(d.volumeTolerance) && d.volumeTolerance > 0))
    return d.volumeTolerance;
  return designIsCloud ? DEVIATION_PC_TOLERANCE_DEFAULT_M : DEVIATION_TOLERANCE_DEFAULT_M;
}

/**
 * Every tag the schema can raise: the union of the two legacy dialogs'
 * `onInvalid` maps, plus the merged form's own (`noResult`, `layerRequired`,
 * `minMustBeLessThanMax`, `deviationNeedsDesign`) and the
 * range checks' (`mustBePositive`, `mustBeInteger`, `outOfRange` — raised as
 * `outOfRangeTag(min, max)`, the bounds after the tag).
 * `stationVolumesRequiredForExpectation` is kept for the shared message map
 * although the merged schema no longer raises it: an expectation now runs on
 * cut & fill, which needs no stations.
 */
export const COMPARE_VALIDATION_TAGS = [
  'noResult',
  'layerRequired',
  'sameLayer',
  'minMustBeLessThanMax',
  'deviationNeedsDesign',
  'mustBePositive',
  'mustBeInteger',
  'outOfRange',
  'alignmentRequired',
  'swathZero',
  'intervalRange',
  'swathRange',
  'offsetBinRange',
  'crossfallBand',
  'crossfallWidth',
  'crossfallSwath',
  'crossfallNoBand',
  'crossfallTolerance',
  'thicknessRequired',
  'toleranceRequired',
  'surfaceRequired',
  'sameSurface',
  'toleranceNegative',
  'failAreaPctRange',
  'stationVolumesRequiredForExpectation',
] as const;
export type CompareValidationTag = (typeof COMPARE_VALIDATION_TAGS)[number];

// ── Shapes (types only) ─────────────────────────────────────────────────────
// Range checks live in `superRefine` below, gated on the result being ticked:
// a stale value behind an unticked result must never block the other one.

const inputsShape = z.object({
  topLayerId: z.string(),
  topSurfaceIds: z.array(z.string()),
  topPcMethod: z.enum(PC_METHODS),
  bottomLayerId: z.string(),
  bottomSurfaceIds: z.array(z.string()),
  bottomPcMethod: z.enum(PC_METHODS),
  boundaryLayerId: z.string(),
});

const expectationShape = z.object(expectationZodShape);

const cutFillShape = z.object({
  resolution: z.number(),
  colormap: z.string(),
  fillHoles: z.number(),
});

const deviationShape = z.object({
  mode: z.enum(DEVIATION_MODES),
  min: z.number(),
  max: z.number(),
  /** Metres; the dialog shows millimetres (spec §12.6). */
  volumeTolerance: z.number(),
  decimate: z.number(),
  verticalThreshold: z.number(),
  kNearestTriangles: z.number(),
  signed: z.boolean(),
  ifcName: z.string(),
  ifcTypes: z.string(),
  tifResolution: z.number(),
  tifStat: z.enum(['mean', 'min', 'max', 'count']),
  /** Empty = the server stamps the project's SRID. */
  tifSrid: z.number().nullable(),
  renderPerSurface: z.boolean(),
  renderImageSize: z.number(),
  renderColormap: z.string(),
  /** Off only in horizontal mode, which has no vertical volume (T2). */
  volumeStats: z.boolean(),
  volumeResolution: z.number(),
  volumePlane: z.enum(['xy', 'fitted']),
  referenceMode: z.enum(['tin', 'surfaces']),
  /** The user picked `referenceMode` — only then is it sent; otherwise the
   *  server's feature-count-aware default applies. */
  referenceModeEdited: z.boolean(),
  referenceResolution: z.number(),
  referenceCellStat: z.enum(['min', 'mean', 'median', 'p05']),
  referenceInterp: z.enum(['nearest', 'bilinear']),
});

const addOnsShape = z.object({
  /** One GeoTIFF switch for both engines (ruling R14, spec §7): cut & fill
   *  `exportTif` and deviation `outTif`. On by default. */
  geotiff: z.boolean(),
  /** DXF of the cut/fill bodies (`exportDxf`); needs cut & fill. */
  dxf: z.boolean(),
  pdf: z.boolean(),
  /** Blank = the run name. */
  reportTitle: z.string(),
  reportNotes: z.string(),
  reportLocale: z.enum(SUPPORTED_REPORT_LOCALES),
});

const shape = z.object({
  kind: z.enum(COMPARE_KINDS),
  results: z.array(z.enum(COMPARE_RESULTS)),
  inputs: inputsShape,
  cutFill: cutFillShape,
  deviation: deviationShape,
  addOns: addOnsShape,
  output: z.object({ name: z.string() }),
  ...stationVolumesZodShape,
  ...expectationZodShape,
});

export type CompareFormValues = z.infer<typeof shape>;

type GroupKey = 'kind' | 'results' | 'inputs' | 'cutFill' | 'deviation' | 'addOns' | 'output';
/** The station-volume and expectation fields that sit at the form root. */
export type CompareRootFields = Omit<CompareFormValues, GroupKey>;

/** Per-group partial values: what defaults, re-runs and couplings hand over. */
export type ComparePatch = Partial<CompareRootFields> & {
  kind?: CompareFormValues['kind'];
  results?: CompareResult[];
  inputs?: Partial<CompareFormValues['inputs']>;
  cutFill?: Partial<CompareFormValues['cutFill']>;
  deviation?: Partial<CompareFormValues['deviation']>;
  addOns?: Partial<CompareFormValues['addOns']>;
  output?: Partial<CompareFormValues['output']>;
};

/** `v` with `patch` merged in, group by group. Never mutates `v`. */
export function applyComparePatch(v: CompareFormValues, patch: ComparePatch): CompareFormValues {
  const { kind, results, inputs, cutFill, deviation, addOns, output, ...root } = patch;
  // A root key present as `undefined` must not wipe a value.
  const rootSet = Object.fromEntries(Object.entries(root).filter(([, x]) => x !== undefined));
  return {
    ...v,
    ...rootSet,
    kind: kind ?? v.kind,
    results: results ? [...results] : v.results,
    inputs: { ...v.inputs, ...inputs },
    cutFill: { ...v.cutFill, ...cutFill },
    deviation: { ...v.deviation, ...deviation },
    addOns: { ...v.addOns, ...addOns },
    output: { ...v.output, ...output },
  };
}

// ── Checks that apply only while their result is ticked ─────────────────────
// Every built-in check carries a TAG, never zod's English default.

const positive = () => z.number().positive({ message: 'mustBePositive' });
const int = () => z.number().int({ message: 'mustBeInteger' });

/**
 * The `outOfRange` tag with the field's bounds riding along —
 * `outOfRange|<min>|<max>`, either side empty when unbounded — so the message
 * can say "Enter a value between 256 and 8192." (`parseValidationTag`).
 */
export const outOfRangeTag = (min?: number, max?: number) => `outOfRange|${min ?? ''}|${max ?? ''}`;
const range = (min?: number, max?: number) => ({ message: outOfRangeTag(min, max) });

/** A schema message split into its tag and, for `outOfRange`, its bounds. */
export function parseValidationTag(message: string): { tag: string; min?: number; max?: number } {
  const [tag, min, max] = message.split('|');
  const num = (v: string | undefined) => (v === undefined || v === '' ? undefined : Number(v));
  return { tag, min: num(min), max: num(max) };
}

const cutFillChecks = z.object({
  resolution: positive(),
  fillHoles: int().min(0, range(0)),
});

const deviationChecks = z
  .object({
    min: z.number(),
    max: z.number(),
    volumeTolerance: positive(),
    decimate: int().min(1, range(1)),
    verticalThreshold: z.number().min(0, range(0, 1)).max(1, range(0, 1)),
    kNearestTriangles: int().min(1, range(1)),
    tifResolution: positive(),
    tifSrid: int().positive({ message: 'mustBePositive' }).nullable(),
    renderImageSize: int().min(256, range(256, 8192)).max(8192, range(256, 8192)),
    volumeResolution: positive(),
    referenceResolution: positive(),
  })
  .refine((d) => d.min < d.max, { message: 'minMustBeLessThanMax', path: ['max'] });

type Path = (string | number)[];

const forward = (ctx: z.RefinementCtx, prefix: Path, result: { success: boolean; error?: z.ZodError }) => {
  if (result.success || !result.error) return;
  for (const issue of result.error.issues)
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...prefix, ...(issue.path as Path)], message: issue.message });
};

const custom = (ctx: z.RefinementCtx, path: Path, message: CompareValidationTag) =>
  ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });

/** Two LandXML picks of the same file are a valid comparison only when both
 *  sides narrow it to surface sets that differ. */
const sameInput = (i: CompareFormValues['inputs']): boolean => {
  if (!i.topLayerId || i.topLayerId !== i.bottomLayerId) return false;
  if (i.topSurfaceIds.length === 0 || i.bottomSurfaceIds.length === 0) return true;
  const top = [...i.topSurfaceIds].sort().join('\n');
  return top === [...i.bottomSurfaceIds].sort().join('\n');
};

export const compareSchema = shape.superRefine((d, ctx) => {
  if (d.results.length === 0) custom(ctx, ['results'], 'noResult');
  // The submit guard behind the card's disabled state (§12.1).
  if (d.kind === 'survey' && d.results.includes('deviation')) custom(ctx, ['results'], 'deviationNeedsDesign');
  if (!d.inputs.topLayerId) custom(ctx, ['inputs', 'topLayerId'], 'layerRequired');
  if (!d.inputs.bottomLayerId) custom(ctx, ['inputs', 'bottomLayerId'], 'layerRequired');
  if (sameInput(d.inputs)) custom(ctx, ['inputs', 'bottomLayerId'], 'sameLayer');

  if (d.results.includes('cutFill')) {
    forward(ctx, ['cutFill'], cutFillChecks.safeParse(d.cutFill));
    // Flat names (R15): the shared refinements file their issues at the very
    // paths the shared field components read.
    refineStationVolumes(d, ctx);
    refineExpectation(d, ctx);
  }
  // Without cut & fill, Volumes by chainage and Expected thickness are not
  // validated at all: they are cut & fill add-ons, which the dialog shows
  // disabled ("Needs Cut & fill volumes — Add it") and strips at submit
  // (ruling R20), like a PDF that cannot be made.

  if (d.results.includes('deviation')) {
    // Horizontal mode reads no tolerance (R22): the payload sends the default
    // for a bad value, so the check is skipped rather than blocking a field
    // the dialog shows disabled.
    const checked =
      d.deviation.mode === 'horizontal'
        ? { ...d.deviation, volumeTolerance: DEVIATION_TOLERANCE_DEFAULT_M }
        : d.deviation;
    forward(ctx, ['deviation'], deviationChecks.safeParse(checked));
  }
});

// ── Defaults ────────────────────────────────────────────────────────────────

/** "A vs B", the name a run starts with (spec §4.2). */
export const defaultCompareName = (a?: { name: string }, b?: { name: string }): string =>
  `${a?.name ?? ''} vs ${b?.name ?? ''}`.trim();

/** The settings every new run starts from, before context, memory or re-run. */
function baseValues(language: string): CompareFormValues {
  return {
    kind: 'design',
    results: ['cutFill'],
    inputs: {
      topLayerId: '',
      topSurfaceIds: [],
      topPcMethod: NEW_RUN_PC_METHOD,
      bottomLayerId: '',
      bottomSurfaceIds: [],
      bottomPcMethod: NEW_RUN_PC_METHOD,
      boundaryLayerId: '',
    },
    cutFill: {
      resolution: CUT_FILL_GRID_DEFAULT_M,
      colormap: defaultRampName('surfaceDifference'),
      fillHoles: 0,
    },
    deviation: {
      mode: 'vertical',
      min: -DEVIATION_RANGE_DEFAULT_M,
      max: DEVIATION_RANGE_DEFAULT_M,
      volumeTolerance: DEVIATION_TOLERANCE_DEFAULT_M,
      decimate: 1,
      verticalThreshold: 0.25,
      kNearestTriangles: 16,
      signed: true,
      ifcName: '',
      ifcTypes: '',
      tifResolution: 0.05,
      tifStat: 'mean',
      tifSrid: null,
      renderPerSurface: true,
      renderImageSize: 2048,
      renderColormap: defaultRampName('deviation'),
      volumeStats: true,
      volumeResolution: 0.05,
      volumePlane: 'xy',
      // A MODEL design's default; `deviationCouplingFor` re-defaults it from
      // the picked design (VECTOR → surfaces) until the user edits it.
      referenceMode: 'tin',
      referenceModeEdited: false,
      referenceResolution: 0.05,
      referenceCellStat: 'min',
      referenceInterp: 'nearest',
    },
    addOns: {
      geotiff: true,
      dxf: false,
      pdf: false,
      reportTitle: '',
      reportNotes: '',
      reportLocale: reportLocaleFor(language),
    },
    output: { name: '' },
    // No alignment registry here: the dialog seeds a re-run's station section
    // through `rerun` (`stationVolumesDefaults` with the live alignments and
    // gate).
    ...stationVolumesDefaults(undefined, new Map(), false),
    // Through the shape so the optional `ExpectationForm` fields come out as
    // the schema's required ones (its own defaults fill any gap).
    ...expectationShape.parse(expectationDefaults()),
  };
}

/** Survey mode has no deviation map (§12.1); an empty pick falls back to
 *  cut & fill, so the defaults are always submittable on that axis. */
const sanitizeResults = (results: readonly CompareResult[], kind: CompareFormValues['kind']): CompareResult[] => {
  const kept = COMPARE_RESULTS.filter((r) => results.includes(r) && !(kind === 'survey' && r === 'deviation'));
  return kept.length > 0 ? kept : ['cutFill'];
};

export interface CompareDefaultsArgs {
  /** The right-clicked layer. A point cloud seeds the As-built (B) in Design
   *  mode and the Later survey (A) in Earlier-survey mode — the scan you
   *  right-click is the new one (ruling R16). Anything else seeds A. */
  contextLayer?: LayerLike & { id: string };
  /** This project's remembered settings (`loadRemembered`). Ignored on a
   *  re-run. */
  remembered?: RememberedSettings;
  /** A re-run's restored values (`rerun.ts`); wins over everything else. */
  rerun?: ComparePatch;
  /** The UI language, for the report locale. Defaults to English. */
  language?: string;
}

/**
 * The dialog's starting values.
 *
 * A re-run restores ITS run and nothing else: remembered settings are
 * ignored, the kind is the run's own (`'design'` for runs made before the
 * field) and so is the sampling (`'min'` for runs that stored none).
 *
 * Results: a re-run's own, else the remembered selection, else Deviation map
 * for a point-cloud context ("check my scan") and Cut & fill otherwise. Only
 * memory or a re-run can preselect both — two heavy jobs are the user's call
 * (spec §4.1).
 */
export function compareDefaults(args: CompareDefaultsArgs = {}): CompareFormValues {
  const { contextLayer, rerun, language = 'en' } = args;
  const remembered = rerun ? undefined : args.remembered;
  let v = baseValues(language);

  if (remembered) {
    const sampling = remembered.sampling;
    v = applyComparePatch(v, {
      kind: remembered.kind,
      inputs: sampling ? { topPcMethod: sampling, bottomPcMethod: sampling } : undefined,
      cutFill: remembered.grid != null ? { resolution: remembered.grid } : undefined,
      deviation: {
        ...(remembered.tolerance != null ? { volumeTolerance: remembered.tolerance } : {}),
        ...(remembered.deviationMode ? { mode: remembered.deviationMode } : {}),
      },
    });
  }
  if (rerun) {
    v = applyComparePatch(v, {
      kind: rerun.kind ?? 'design',
      inputs: {
        topPcMethod: rerun.inputs?.topPcMethod ?? LEGACY_PC_METHOD,
        bottomPcMethod: rerun.inputs?.bottomPcMethod ?? LEGACY_PC_METHOD,
      },
    });
  }

  const contextIsCloud = !!contextLayer && isPointcloudLayer(contextLayer);
  if (contextLayer) {
    const seedsTop = !contextIsCloud || v.kind === 'survey';
    v = applyComparePatch(v, {
      inputs: seedsTop ? { topLayerId: contextLayer.id } : { bottomLayerId: contextLayer.id },
    });
  }

  if (rerun) v = applyComparePatch(v, rerun);

  const results = rerun?.results ?? remembered?.results ?? (contextIsCloud ? ['deviation'] : ['cutFill']);
  return { ...v, results: sanitizeResults(results, v.kind) };
}
